/* eslint-disable no-underscore-dangle */
import axios from 'axios';
import ua from '@tencent/docs-user-agent';
import { RUNTIME } from '@src/slide/infrastructure/module/dependent/config/tob/constant';

// 复制粘贴接口
const clipboardApi: string[] = ['getClipboardData', 'setClipboardData'];

// 图片上传接口
const imageUploadApi: string[] = ['chooseImage', 'getLocalImgData'];

const jsApiList: string[] = ([] as string[]).concat(imageUploadApi, clipboardApi);

class WxConfigService {
    // wx 接口鉴权相关信息
    private jssdk = RUNTIME.LIBRARY.WX_JS_SDK_1_6_0;
    private appId = 'wx46dfe750eef96da7';

    public async init() {
        if (ua.isWeChat || ua.isWxMiniProgram) {
            // 只在微信 h5 和微信小程序中加载 jsapi
            await this.setWxJsApi();
        }
    }

    /**
     * 是否已加载 wxsdk 接口，防止重复请求
     */
    public hasLoadedSDK = () => {
        if (typeof window.wx !== 'undefined' && window.WeixinJSBridge) {
            return true;
        }
    };

    /**
     * 加载微信 sdk 接口
     */
    public async loadJSSDK() {
        if (this.hasLoadedSDK()) {
            return;
        }
        await this.loadScript();
    }

    /**
     * 配置微信接口，返回值是一个异步操作，配置失败将 reject，务必 catch 住错误信息
     * @param apiList api 接口名称数组
     */
    private async setWxJsApi() {
        try {
            window.log.info('get in wx init setWxJsApi');
            /**
           * 1. 加载 jssdk 前确保 dom 节点已经 ready
           * 2. 设置接口前先加载 jssdk，如果 jssdk 已加载会直接 resolve，不必担心重复加载问题
           * 3. 记载完 sdk 后，将获取签名配置参数，同上，已获取则直接返回参数，不必东西重复获取
           * 4. 获取参数成功后进行 config 配置
           */
            await this.waitingForDomMounted();
            await this.loadJSSDK();
            const signatureData = await this.getSignature();
            const { noncestr, signature, timestamp } = signatureData;
            window.wx.config({
                debug: false,
                appId: this.appId,
                nonceStr: noncestr,
                timestamp,
                signature,
                jsApiList,
            });
            // 失败则 reject 错误信息
            window.wx.error(res =>  window.log.info(`wx set jsApiList fail, error msg: ${JSON.stringify(res)}`));
            window.wx.ready(() => window.log.info('wx setWxJsApi success'));
        } catch (err) {
            window.log.error('setWxJsApi init error', err);
        }
    }

    /**
     * 等待 dom 节点完全挂载成功
     */
    private waitingForDomMounted = () => new Promise<void>((resolve) => {
        if (window?.document?.readyState) {
            const { readyState } = window.document;
            if (readyState === 'complete') {
                resolve();
            }
            if (readyState === 'loading' || readyState === 'interactive') {
                window.document.addEventListener('readystatechange', () => {
                    if (document.readyState === 'complete') {
                        resolve();
                    }
                });
            }
        } else {
            resolve();
        }
    });

    private loadScript = () => new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = this.jssdk;
        document.head.appendChild(script);
        script.onload = resolve;
        script.onerror = reject;
    });

    /**
     * 获取微信签名
     */
    private async getSignature() {
        const res = await axios.get('/v2/util/wechat/signature', {
            withCredentials: true,
            params: {
                url: this.getPageOriginHref(),
            },
        });
        const { cgicode, retcode, msg } = res.data;
        if (cgicode !== 0 || retcode !== 0) {
            throw new Error(`wx get signature error, retcode: ${retcode}, error msg: ${msg}`);
        }
        return res.data;
    }

    /**
     * 微信校验的 url 必须要和原始链接对应上，不能包含后面在 url 中添加的 query
     */
    private getPageOriginHref() {
        /**
         * 2022-01-17
         * 微信接口人: ifwang
         * 微信小程序校验的 url： 安卓是双签，在刚打开页面或者 onload 之后，两个都可以
         *                    ios 只能在 onload 事件之后
         * 普通网页 h5:
         * 在网页内容开始加载的时候获取的 url，类似于 reponseEnd 或者 domLoading 的时机，也就是初始化
         */

        if (ua.isWeChat && window.__originUrl) {
            return window.__originUrl;
        }

        return window.location.href;
    }
}


const wxConfigService = new WxConfigService();
export default wxConfigService;
